import axios from "axios";
import { updateProfile } from "firebase/auth";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { soulManager } from "../../data/managers/SoulManager";
import { DocumentTypeEnum } from "../../data/models/applications/CreditApplication";
import {
  SoulDataModel,
  UpdateSoulInfoPayload,
} from "../../data/models/Soul/SoulDataModel";
import { useAuth } from "../../data/providers/AuthProvider";
import { useUserData } from "../../data/providers/UserDataProvider";
import { storageService } from "../../firebase-config";
import { validateDocumentByType } from "../../utils/FormsTool";

type ProfileForm = {
  fullName: string;
  documentType?: DocumentTypeEnum;
  document?: string;
  address?: string;
  emailOptional?: string;
  birthDate?: string;
  docPhoto?: File | null;
  selfiePhoto?: File | null;
};

type FormErrors = Partial<Record<keyof ProfileForm, string>>;

const isFutureDate = (iso?: string) => {
  if (!iso) return false;
  const d = new Date(iso);
  const today = new Date();
  d.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return d.getTime() > today.getTime();
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

function validateForm(f: ProfileForm): FormErrors {
  const errors: FormErrors = {};

  if ((f.fullName || "").trim().length === 0) {
    errors.fullName = "Ingresa tu nombre o deja Anónimo";
  } else if (f.fullName.trim().length < 2) {
    errors.fullName = "Debe tener al menos 2 caracteres";
  } else if (f.fullName.length > 80) {
    errors.fullName = "Máximo 80 caracteres";
  }

  const docErr = validateDocumentByType(f.documentType, f.document);
  if (docErr) errors.document = docErr;

  if (f.address && f.address.length > 120) {
    errors.address = "Máximo 120 caracteres";
  }

  if (f.emailOptional && !emailRegex.test(f.emailOptional)) {
    errors.emailOptional = "Correo inválido";
  } else if (f.emailOptional && f.emailOptional.length > 120) {
    errors.emailOptional = "Máximo 120 caracteres";
  }

  if (isFutureDate(f.birthDate)) {
    errors.birthDate = "La fecha no puede ser futura";
  }

  if (!f.birthDate || f.birthDate.trim() === "") {
    errors.birthDate = "La fecha de nacimiento es obligatoria";
  } else if (isFutureDate(f.birthDate)) {
    errors.birthDate = "La fecha no puede ser futura";
  }








  return errors;
}

export function useProfile() {
  const userData = useUserData()!;
  const { user, refreshUser } = useAuth();

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docFileRef = useRef<HTMLInputElement>(null);
  const selfieFileRef = useRef<HTMLInputElement>(null);
  const [docFile, setDocFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [docPreview, setDocPreview] = useState<string | null>(null);
  const [selfiePreview, setSelfiePreview] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);

  const initialForm = useMemo<ProfileForm>(() => {
    const data = userData.data;
    return {
      fullName: data?.fullName?.trim() || "Anónimo",
      documentType: data?.documentType || DocumentTypeEnum.CC,
      document: data?.document || "",
      address: data?.address || "",
      emailOptional: data?.emailOptional || "",
      birthDate: data?.birthDate || "",
    };
  }, [userData]);

  useEffect(() => {
    const data = userData.data;
    if (data?.docPhotoURL) {
      urlToFile(data.docPhotoURL, "doc.jpg").then((file) => {
        if (file) {
          updateDocFile(file);
        }
      });
    }
    if (data?.selfiePhotoURL) {
      urlToFile(data.selfiePhotoURL, "selfie.jpg").then((file) => {
        if (file) {
          updateSelfieFile(file);
        }
      });
    }
  }, [userData]);

  const [form, setForm] = useState<ProfileForm>(initialForm);

  useEffect(() => {
    setForm(initialForm);
    setErrors({});
  }, [initialForm]);

  useEffect(() => {
    if (docFile && selfieFile) {
      validateForm({
        ...form,
        docPhoto: docFile,
        selfiePhoto: selfieFile,
      });
    }
    setErrors({});
  }, [docFile, selfieFile]);

  const handleChange =
    (field: keyof ProfileForm, file?: File | null) =>
    (e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = file ?? e!.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    };

  const handleBlur =
    (field: keyof ProfileForm, file?: File | null) =>
    (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = { ...form, [field]: file ?? e!.target.value };
      const v = validateForm(next);
      setErrors(v);
    };

  const handlePickPhoto = () => fileInputRef.current?.click();

  const handleUploadPhoto = async (file: File) => {
    if (!user) return;
    setUploading(true);
    try {
      const avatarRef = ref(storageService, `users/${user.uid}/avatar.jpg`);
      await uploadBytes(avatarRef, file);
      const url = await getDownloadURL(avatarRef);
      await updateProfile(user, { photoURL: url });
      await refreshUser();
      handleReload();
    } catch (error) {
      console.error("❌ Error subiendo foto:", error);
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleUploadPhoto(f);
  };

  async function urlToFile(
    url: string,
    filename: string
  ): Promise<File | null> {
    try {
      const response = await axios.get(url, { responseType: "blob" });
      const blob = response.data as Blob;
      const mimeType =
        blob.type ||
        response.headers["content-type"] ||
        "application/octet-stream";
      return new File([blob], filename, { type: mimeType });
    } catch (err) {
      return null;
    }
  }

  const handlePickDoc = (e?: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) {
      updateDocFile(file);
    } else {
      docFileRef.current?.click();
    }
  };

  const handlePickSelfie = (e?: React.ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (file) {
      updateSelfieFile(file);
    } else {
      selfieFileRef.current?.click();
    }
  };

  function updateDocFile(file: File) {
    if (!file) return;
    setDocFile(file);
    setDocPreview(URL.createObjectURL(file));
    handleChange("docPhoto", file)();
    handleBlur("docPhoto", file)();
  }

  function updateSelfieFile(file: File) {
    if (!file) return;
    setSelfieFile(file);
    setSelfiePreview(URL.createObjectURL(file));
    handleChange("selfiePhoto", file)();
    handleBlur("selfiePhoto", file)();
  }

  const handleSave = async () => {
    if (!user?.uid) return;

    if (userData.data?.isVerified) {
      const v: FormErrors = {};
      if (!form.address) v.address = "La dirección es requerida";
      if (form.emailOptional && !emailRegex.test(form.emailOptional)) {
        v.emailOptional = "Correo inválido";
      }
      setErrors(v);
      if (Object.keys(v).length > 0) return;

      setSaving(true);
      try {
        const soulData: SoulDataModel = {
          address: form.address?.trim() || null!,
          emailOptional: form.emailOptional?.trim() || null!,
        };
        const payload = new UpdateSoulInfoPayload(soulData);
        await soulManager.updateSoulInfo(payload);
        handleReload();
      } finally {
        setSaving(false);
      }
      return;
    }

    const v = validateForm(form);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    if (!docFile || !selfieFile) {
      if (!userData.data?.docPhotoURL || !userData.data?.selfiePhotoURL) {
        setErrors((prev) => ({
          ...prev,
          photos: "Debes subir foto del documento y selfie.",
        }));
        return;
      }
    }

    setSaving(true);
    try {
      const docRef = ref(storageService, `users/${user.uid}/doc.jpg`);
      let docUrl = userData.data?.docPhotoURL || "";
      let selfieUrl = userData.data?.selfiePhotoURL || "";

      if (docFile) {
        const docRef = ref(storageService, `users/${user.uid}/doc.jpg`);
        await uploadBytes(docRef, docFile);
        docUrl = await getDownloadURL(docRef);
      }

      if (selfieFile) {
        const selfieRef = ref(storageService, `users/${user.uid}/selfie.jpg`);
        await uploadBytes(selfieRef, selfieFile);
        selfieUrl = await getDownloadURL(selfieRef);
      }

      const soulData: SoulDataModel = {
        fullName: form.fullName?.trim() || "Anónimo",
        documentType: form.documentType || null!,
        document: form.document?.trim() || null!,
        address: form.address?.trim() || null!,
        emailOptional: form.emailOptional?.trim() || null!,
        birthDate: form.birthDate || null!,
        docPhotoURL: docUrl,
        selfiePhotoURL: selfieUrl,
        isVerified: false,
      };
      const payload = new UpdateSoulInfoPayload(soulData);
      await soulManager.updateSoulInfo(payload);
      handleReload();
    } finally {
      setSaving(false);
    }
  };

  const handleReload = async () => {
    if (!user?.uid) return;
    setReloading(true);
    try {
      await userData.reloadUserData();
      setErrors({});
    } finally {
      setReloading(false);
    }
  };

  return {
    user,
    userData,
    form,
    handlePickPhoto,
    fileInputRef,
    uploading,
    reloading,
    handleChange,
    handleBlur,
    errors,
    setErrors,
    setForm,
    saving,
    handleSave,
    initialForm,
    docFileRef,
    selfieFileRef,
    docPreview,
    selfiePreview,
    handlePickDoc,
    handlePickSelfie,
    uploadingDoc,
    uploadingSelfie,
    handlePhotoInput,
  };
}
